// Deep hardware fingerprint capture.
//
// FingerprintJS OSS returns a composite `visitorId`. That works well until
// the visitor uses a VPN, clears cookies, opens incognito, or changes browser
// — any of which can perturb enough signals to slide the composite hash off.
//
// For VPN-proof duplicate detection we ALSO capture three hardware-level
// signals as their own stable hashes so the backend can match on any single
// one:
//
//   fp_canvas_hash    — Canvas 2D rendering signature (varies by GPU + font
//                       stack + subpixel rendering)
//   fp_webgl_hash     — WebGL vendor/renderer + a handful of stable params
//                       (locked to the physical GPU regardless of browser)
//   fp_hardware_hash  — Screen resolution + color depth + hardware concurrency
//                       + device memory + timezone + platform + CPU class.
//                       Survives cookie clears, browser swaps, and VPNs.
//
// All hashing uses Web Crypto SHA-256 → first 24 hex chars (≈96 bits).
// Everything is best-effort; on any failure we return `""` and the backend
// treats the field as absent.

const sha256Hex = async (input) => {
  try {
    const enc = new TextEncoder().encode(input);
    const buf = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 24);
  } catch {
    return "";
  }
};

// -------------------------------------------------------------------------- //
// Canvas hash — a deterministic drawing whose PNG bytes vary by GPU / font
// -------------------------------------------------------------------------- //
const canvasSignature = () => {
  try {
    const c = document.createElement("canvas");
    c.width = 300;
    c.height = 80;
    const ctx = c.getContext("2d");
    if (!ctx) return "";
    // Mix in geometry + text so both raster + font stack contribute.
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.font = "11pt Arial";
    ctx.fillText("Fingerprint · 你好, 🌍", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.font = "18pt Arial";
    ctx.fillText("Fingerprint · 你好, 🌍", 4, 45);
    // Add a compound shape whose antialiasing depends on the GPU.
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = "rgb(255,0,255)";
    ctx.beginPath();
    ctx.arc(50, 50, 40, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "rgb(0,255,255)";
    ctx.beginPath();
    ctx.arc(100, 50, 40, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();
    return c.toDataURL();
  } catch {
    return "";
  }
};

// -------------------------------------------------------------------------- //
// WebGL hash — pulls the vendor + renderer + a few immutable numeric limits
// -------------------------------------------------------------------------- //
const webglSignature = () => {
  try {
    const c = document.createElement("canvas");
    const gl =
      c.getContext("webgl2") ||
      c.getContext("webgl") ||
      c.getContext("experimental-webgl");
    if (!gl) return "";
    const parts = [];
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbg) {
      parts.push(String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || ""));
      parts.push(String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || ""));
    }
    parts.push(String(gl.getParameter(gl.VENDOR) || ""));
    parts.push(String(gl.getParameter(gl.RENDERER) || ""));
    parts.push(String(gl.getParameter(gl.VERSION) || ""));
    parts.push(String(gl.getParameter(gl.SHADING_LANGUAGE_VERSION) || ""));
    // Stable-across-drivers numeric caps that describe the physical GPU.
    const caps = [
      gl.MAX_TEXTURE_SIZE,
      gl.MAX_VIEWPORT_DIMS,
      gl.MAX_RENDERBUFFER_SIZE,
      gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS,
      gl.MAX_VARYING_VECTORS,
    ];
    caps.forEach((k) => {
      try {
        const v = gl.getParameter(k);
        parts.push(Array.isArray(v) ? v.join("x") : String(v));
      } catch {
        /* skip */
      }
    });
    return parts.join("|");
  } catch {
    return "";
  }
};

// -------------------------------------------------------------------------- //
// Audio fingerprint — renders a known waveform through a compressor and hashes
// the sample output. The exact float values depend on the browser's audio
// engine + OS DSP → highly stable per physical machine.
// -------------------------------------------------------------------------- //
const audioSignature = async () => {
  try {
    const AudioCtx =
      window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!AudioCtx) return "";
    const ctx = new AudioCtx(1, 44100, 44100);
    const oscillator = ctx.createOscillator();
    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(10000, ctx.currentTime);
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-50, ctx.currentTime);
    compressor.knee.setValueAtTime(40, ctx.currentTime);
    compressor.ratio.setValueAtTime(12, ctx.currentTime);
    compressor.attack.setValueAtTime(0, ctx.currentTime);
    compressor.release.setValueAtTime(0.25, ctx.currentTime);
    oscillator.connect(compressor);
    compressor.connect(ctx.destination);
    oscillator.start(0);
    const buffer = await ctx.startRendering();
    const samples = buffer.getChannelData(0);
    // Sum a stable slice of samples for a compact hash source.
    let sum = 0;
    for (let i = 4500; i < 5000; i++) sum += Math.abs(samples[i] || 0);
    return sum.toString();
  } catch {
    return "";
  }
};

// -------------------------------------------------------------------------- //
// Font list — detect which fonts are installed via canvas text width probing
// -------------------------------------------------------------------------- //
const fontSignature = () => {
  try {
    const testString = "mmMwWLliI0O&1";
    const testSize = "72px";
    const fallbacks = ["monospace", "sans-serif", "serif"];
    const candidates = [
      "Arial", "Arial Black", "Arial Narrow", "Comic Sans MS", "Courier",
      "Courier New", "Georgia", "Impact", "Lucida Console", "Palatino",
      "Times", "Times New Roman", "Trebuchet MS", "Verdana", "Helvetica",
      "Helvetica Neue", "Tahoma",
      // OS-specific
      "Menlo", "Monaco", "SF Pro Text",           // macOS
      "Segoe UI", "Consolas", "Calibri",           // Windows
      "Ubuntu", "Cantarell", "DejaVu Sans",        // Linux
      "PingFang SC", "Microsoft YaHei",            // Chinese
      "Hiragino Sans", "Noto Sans JP",             // Japanese
    ];
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    const baseWidths = {};
    for (const fb of fallbacks) {
      ctx.font = `${testSize} ${fb}`;
      baseWidths[fb] = ctx.measureText(testString).width;
    }
    const detected = [];
    for (const font of candidates) {
      for (const fb of fallbacks) {
        ctx.font = `${testSize} "${font}", ${fb}`;
        const w = ctx.measureText(testString).width;
        if (w !== baseWidths[fb]) {
          detected.push(font);
          break;
        }
      }
    }
    return detected.join(",");
  } catch {
    return "";
  }
};

// -------------------------------------------------------------------------- //
// Media device inventory — kind + groupId across cameras / mics / speakers.
// deviceId is empty without user permission (privacy), but the number of
// devices per kind + groupId groupings is still highly discriminating.
// -------------------------------------------------------------------------- //
const mediaDevicesSignature = async () => {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return "";
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .map((d) => `${d.kind}:${d.groupId || ""}`)
      .sort()
      .join("|");
  } catch {
    return "";
  }
};
const hardwareSignature = () => {
  try {
    const parts = [
      screen.width,
      screen.height,
      screen.colorDepth,
      screen.pixelDepth,
      window.devicePixelRatio,
      navigator.hardwareConcurrency || 0,
      navigator.deviceMemory || 0,
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      navigator.platform || "",
      navigator.language || "",
      (navigator.languages || []).join(","),
      navigator.maxTouchPoints || 0,
    ];
    return parts.map(String).join("|");
  } catch {
    return "";
  }
};

/**
 * Collect all deep hashes in parallel plus TWO composite IDs:
 *
 *   hardware_id       — SHA-256(canvas || webgl || hardware profile).
 *                       A collision means the same GPU + screen/CPU/timezone
 *                       profile — very strong duplicate signal.
 *
 *   super_hardware_id — SHA-256(cpu cores || RAM || audio fingerprint ||
 *                       installed fonts || screen resolution || media device
 *                       inventory). This blends browser-agnostic hardware
 *                       traits (audio engine output, OS-installed fonts, screen
 *                       layout, physical mics/cams) into one key. A collision
 *                       is treated as a **100% CONFIRMED DUPLICATE** — no
 *                       realistic collision path across two different physical
 *                       machines using different OS installs.
 *
 * Silently returns empty strings for any signal the browser blocks.
 */
export const collectHardwareFingerprint = async () => {
  const [canvasRaw, webglRaw, hardwareRaw, audioRaw, fontsRaw, mediaRaw] =
    await Promise.all([
      Promise.resolve(canvasSignature()),
      Promise.resolve(webglSignature()),
      Promise.resolve(hardwareSignature()),
      audioSignature(),
      Promise.resolve(fontSignature()),
      mediaDevicesSignature(),
    ]);

  // Extract the strict discrete traits that go into super_hardware_id — these
  // stay stable across browser sessions on the same physical machine.
  const cpuCores = navigator.hardwareConcurrency || 0;
  const ramGb = navigator.deviceMemory || 0;
  let screenRes = "";
  try {
    screenRes = `${screen.width}x${screen.height}x${screen.colorDepth}`;
  } catch {
    /* headless / weird env — leave blank */
  }

  const superRaw = [
    `cpu=${cpuCores}`,
    `ram=${ramGb}`,
    `audio=${audioRaw}`,
    `fonts=${fontsRaw}`,
    `screen=${screenRes}`,
    `media=${mediaRaw}`,
  ].join("||");

  const [
    fp_canvas_hash,
    fp_webgl_hash,
    fp_hardware_hash,
    hardware_id,
    super_hardware_id,
    fp_audio_hash,
    fp_fonts_hash,
    fp_media_hash,
  ] = await Promise.all([
    canvasRaw ? sha256Hex(canvasRaw) : "",
    webglRaw ? sha256Hex(webglRaw) : "",
    hardwareRaw ? sha256Hex(hardwareRaw) : "",
    canvasRaw || webglRaw || hardwareRaw
      ? sha256Hex(`${canvasRaw}::${webglRaw}::${hardwareRaw}`)
      : "",
    sha256Hex(superRaw),
    audioRaw ? sha256Hex(audioRaw) : "",
    fontsRaw ? sha256Hex(fontsRaw) : "",
    mediaRaw ? sha256Hex(mediaRaw) : "",
  ]);

  return {
    fp_canvas_hash,
    fp_webgl_hash,
    fp_hardware_hash,
    fp_audio_hash,
    fp_fonts_hash,
    fp_media_hash,
    hardware_id,
    super_hardware_id,
  };
};
